import { supabase } from '../config/supabase';
import { Club, UserClub, ClubWithStats } from '../types';

export const clubService = {
  /**
   * Fetch all active clubs
   */
  async getClubs(): Promise<Club[]> {
    const { data, error } = await supabase
      .from('clubs')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (error) throw error;
    return data || [];
  },

  /**
   * Fetch single club with statistics
   * Uses clubs_with_stats view for optimized performance (single query)
   */
  async getClubById(id: string): Promise<ClubWithStats | null> {
    const { data, error } = await supabase
      .from('clubs_with_stats')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }

    return data;
  },

  /**
   * Get user's club memberships with club details
   * @param userId - The user ID
   * @param minimal - If true, only fetch id and name (default: true for performance)
   */
  async getUserClubs(userId: string, minimal = true): Promise<UserClub[]> {
    const clubFields = minimal ? 'id, name' : '*';

    const { data, error } = await supabase
      .from('user_clubs')
      .select(`
        *,
        club:clubs(${clubFields})
      `)
      .eq('user_id', userId)
      .order('joined_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Check if user is a member of a specific club
   */
  async isUserMember(clubId: string, userId?: string): Promise<boolean> {
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;
      userId = user.id;
    }

    const { data, error } = await supabase
      .from('user_clubs')
      .select('user_id')
      .eq('user_id', userId)
      .eq('club_id', clubId)
      .maybeSingle();

    if (error) throw error;
    return !!data;
  },

  /**
   * Join a club as a member
   */
  async joinClub(clubId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('user_clubs')
      .insert({
        user_id: user.id,
        club_id: clubId,
        role: 'member',
      });

    if (error) {
      // Handle duplicate membership gracefully
      if (error.code === '23505') {
        throw new Error('You are already a member of this club');
      }
      throw error;
    }
  },

  /**
   * Leave a club
   */
  async leaveClub(clubId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('user_clubs')
      .delete()
      .eq('user_id', user.id)
      .eq('club_id', clubId);

    if (error) throw error;
  },

  /**
   * Associate user with multiple clubs (batch operation)
   * Used during registration or bulk club joining
   */
  async associateUserWithClubs(userId: string, clubIds: string[]): Promise<void> {
    if (!clubIds || clubIds.length === 0) return;

    const clubMemberships = clubIds.map(clubId => ({
      user_id: userId,
      club_id: clubId,
      role: 'member' as const,
    }));

    const { error } = await supabase
      .from('user_clubs')
      .insert(clubMemberships);

    if (error) {
      // Handle duplicate memberships gracefully
      if (error.code === '23505') {
        throw new Error('You are already a member of one or more of these clubs');
      }
      throw error;
    }
  },

  /**
   * Search clubs by name with pagination
   */
  async searchClubs(
    query: string,
    options?: { offset?: number; limit?: number }
  ): Promise<{ data: Club[]; hasMore: boolean }> {
    const offset = options?.offset || 0;
    const limit = options?.limit || 20;

    // Fetch limit + 1 to determine if there are more results
    const { data, error } = await supabase
      .from('clubs')
      .select('*')
      .ilike('name', `%${query}%`)
      .order('name')
      .range(offset, offset + limit); // Fetch one extra

    if (error) throw error;

    const clubs = data || [];
    const hasMore = clubs.length > limit;

    // Return only the requested limit
    return {
      data: hasMore ? clubs.slice(0, limit) : clubs,
      hasMore,
    };
  },

  /**
   * Get clubs with pagination (no search filter)
   */
  async getClubsPaginated(options?: {
    offset?: number;
    limit?: number;
  }): Promise<{ data: Club[]; hasMore: boolean }> {
    const offset = options?.offset || 0;
    const limit = options?.limit || 20;

    const { data, error } = await supabase
      .from('clubs')
      .select('*')
      .order('name')
      .range(offset, offset + limit);

    if (error) throw error;

    const clubs = data || [];
    const hasMore = clubs.length > limit;

    return {
      data: hasMore ? clubs.slice(0, limit) : clubs,
      hasMore,
    };
  },

  /**
   * Get recent listings for a club
   */
  async getClubListings(clubId: string, limit: number = 10) {
    const { data, error } = await supabase
      .from('clothing_items')
      .select(`
        *,
        seller:profiles(*)
      `)
      .eq('club_id', clubId)
      .eq('status', 'available')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  /**
   * Update club information
   * Requires platform admin or club manager permissions
   */
  async updateClub(clubId: string, updates: Partial<Club>): Promise<void> {
    const { error } = await supabase
      .from('clubs')
      .update(updates)
      .eq('id', clubId);

    if (error) throw error;
  },
};
